import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { M } from "../marketplace/marketplaceTokens";

const pluralize = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Small rounded label used for the category on a row. */
export function CategoryPill({ label }) {
  if (!label) return null;
  return (
    <Box component="span" sx={{ fontSize: "12px", fontWeight: 700, px: "9px", py: "3px", borderRadius: "999px", bgcolor: M.surface2, color: M.ink2 }}>{label}</Box>
  );
}

function Facet({ title, options, value, onToggle }) {
  return (
    <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
      <Box component="legend" sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: ".025em", textTransform: "uppercase", color: M.ink3, mb: "8px", p: 0 }}>{title}</Box>
      {options.map(([v, n]) => (
        <Box key={v} component="label" sx={{ display: "grid", gridTemplateColumns: "18px minmax(0,1fr) auto", gap: "10px", alignItems: "center", fontSize: "14px", p: "6px 8px", borderRadius: "6px", cursor: "pointer", "&:hover": { bgcolor: M.surface2 } }}>
          <Box component="input" type="checkbox" checked={value.includes(v)} onChange={() => onToggle(v)} sx={{ m: 0, width: 16, height: 16, accentColor: M.primary }} />
          <span>{v}</span>
          <Box component="span" sx={{ fontFamily: "var(--font-mono)", fontSize: "11.5px", color: M.ink3 }}>{n}</Box>
        </Box>
      ))}
    </Box>
  );
}

/**
 * One searchable, sortable, filterable list of bank items: search + sort on top, facets on the left, rows on the right.
 *
 *   items    the rows to show.
 *   noun     singular name for the count line ("skill sheet" → "3 skill sheets").
 *   facets   [{ key, title, fn(item) → string, order? }]; counts are computed from `items`.
 *   sorts    [[key, label, compare(a, b)]]; the first one is the default.
 *   searchText(item) → string searched by the box.
 *   renderRow(item) → { top, title, description, meta, side, onOpen, testId } — the page owns what a row says and does.
 */
export default function KbCatalog({ items, noun, searchPlaceholder, facets, sorts, searchText, renderRow }) {
  const empty = useMemo(() => Object.fromEntries(facets.map((f) => [f.key, []])), [facets]);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState(empty);
  const [sort, setSort] = useState(sorts[0][0]);

  const toggle = (key) => (v) => setPicked((p) => ({ ...p, [key]: p[key].includes(v) ? p[key].filter((x) => x !== v) : [...p[key], v] }));
  const clear = () => { setPicked(empty); setQ(""); };
  const anyFilter = Object.values(picked).some((a) => a && a.length);

  const optionsFor = (f) => {
    const values = f.order || Array.from(new Set(items.map(f.fn).filter(Boolean))).sort((a, b) => a.localeCompare(b));
    return values.map((v) => [v, items.filter((i) => f.fn(i) === v).length]).filter(([, n]) => n > 0);
  };

  const compare = (sorts.find((s) => s[0] === sort) || sorts[0])[2];
  const needle = q.trim().toLowerCase();
  const list = items
    .filter((i) => facets.every((f) => !(picked[f.key] || []).length || picked[f.key].includes(f.fn(i)))
      && (!needle || searchText(i).toLowerCase().includes(needle)))
    .sort(compare);

  return (
    <>
      <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", mb: "4px" }}>
        <Box sx={{ position: "relative", flex: "1 1 360px", minWidth: 220, maxWidth: 520 }}>
          <Box component="svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" sx={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: M.ink3 }}>
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </Box>
          <Box component="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder} aria-label={searchPlaceholder}
            sx={{ width: "100%", height: 44, pl: "40px", pr: "14px", border: `1px solid ${M.lineCool}`, borderRadius: "6px", font: "inherit", fontSize: "15px", bgcolor: M.surface, "&:focus": { outline: "none", borderColor: M.primary, boxShadow: "0 0 0 3px var(--color-ring-focus)" } }} />
        </Box>
        <Box component="select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort"
          sx={{ height: 44, px: "14px", border: `1px solid ${M.lineCool}`, borderRadius: "6px", font: "inherit", fontSize: "15px", color: M.ink, bgcolor: M.surface, cursor: "pointer", minWidth: 200, "&:focus": { outline: "none", borderColor: M.primary, boxShadow: "0 0 0 3px var(--color-ring-focus)" } }}>
          {sorts.map((s) => <option key={s[0]} value={s[0]}>Sort: {s[1]}</option>)}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", md: "240px minmax(0,1fr)" }, gap: { xs: "20px", md: "32px" }, mt: "20px", alignItems: "start" }}>
        <Box component="aside" sx={{ position: { md: "sticky" }, top: 20, display: "flex", flexDirection: "column", gap: "22px" }}>
          {facets.map((f) => <Facet key={f.key} title={f.title} value={picked[f.key] || []} onToggle={toggle(f.key)} options={optionsFor(f)} />)}
          {anyFilter && <Box component="button" onClick={clear} sx={{ alignSelf: "flex-start", border: 0, bgcolor: "transparent", p: 0, font: "inherit", fontSize: "13px", fontWeight: 600, color: M.primary, cursor: "pointer", textDecoration: "underline" }}>Clear filters</Box>}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: "13px", color: M.ink3, mb: "12px" }} aria-live="polite">{pluralize(list.length, noun)}</Box>
          {list.length === 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", textAlign: "center", p: "56px 20px", border: `1.5px dashed ${M.lineCool}`, borderRadius: "12px" }}>
              <Box sx={{ fontSize: "16px", fontWeight: 600 }}>{`No ${noun}s match.`}</Box>
              <Box sx={{ fontSize: "14px", color: M.ink3, mb: "8px" }}>Try a different search or remove a filter.</Box>
              <Box component="button" onClick={clear} sx={{ height: 36, px: "14px", borderRadius: "8px", border: `1px solid ${M.lineCool}`, bgcolor: M.surface, font: "inherit", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", cursor: "pointer" }}>Clear filters</Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", bgcolor: M.surface, border: `1px solid ${M.line}`, borderRadius: "12px", boxShadow: M.shadowSm }}>
              {list.map((item, i) => {
                const r = renderRow(item);
                return (
                  <Box key={item.id} data-testid={r.testId} onClick={r.onOpen}
                    sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "minmax(0,1fr) auto" }, gap: "20px", alignItems: "center", p: "18px 20px", cursor: r.onOpen ? "pointer" : "default", borderTop: i ? `1px solid ${M.line}` : "none", "&:hover": { bgcolor: "#faf9fb" } }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>{r.top}</Box>
                      <Box component="h3" sx={{ m: "6px 0 0", fontSize: "16px", fontWeight: 600, lineHeight: 1.35, fontFamily: "var(--font-display)" }}>{r.title}</Box>
                      {r.description && <Box sx={{ mt: "4px", fontSize: "13.5px", lineHeight: 1.45, color: M.ink3, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{r.description}</Box>}
                      {r.meta && <Box sx={{ display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap", mt: "10px", fontSize: "13px", color: M.ink3 }}>{r.meta}</Box>}
                      {r.facts && <Box sx={{ mt: "8px" }}>{r.facts}</Box>}
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", sm: "flex-end" }, gap: "6px", maxWidth: { sm: 320 } }}>
                      {r.figure}
                      <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: { sm: "flex-end" }, alignItems: "center" }}>{r.actions}</Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

/** The big number + caption shown on the right of a row ("12 steps" / "Pass 80 pts"). */
export function RowFigure({ value, unit, caption }) {
  return (
    <Box sx={{ textAlign: { sm: "right" } }}>
      <Box sx={{ fontSize: "13px", color: M.ink3 }}><Box component="b" sx={{ fontFamily: "var(--font-display)", fontSize: "22px", color: M.ink, mr: "4px" }}>{value}</Box>{unit}</Box>
      {caption && <Box sx={{ fontSize: "12.5px", color: M.ink3 }}>{caption}</Box>}
    </Box>
  );
}
